// The client environment's place in the account menu (#10718 slice 3).
//
// The menu the sidebar's account block opens, where Log out is. In a client
// tab it holds everything done to the grant from inside: time left, renewing
// in the last half hour, admin access (the one step that takes a reason, on
// the staff member's own session), and leaving the client for the staff
// console without logging out. In a home tab, while the staff member has a
// client open, it offers to continue in it here. It is the only place in the
// everyday app that names a client, and it names only the one already open:
// client success shares this screen with clients, and our client list is
// confidential (slice 1b).
import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowSquareInIcon,
  ArrowsClockwiseIcon,
  HouseIcon,
  ShieldStarIcon,
} from "@phosphor-icons/react";
import fpFetch from "@/services/fpFetch";
import {
  clientEnvironmentOpenUrl,
  goHome,
  STAFF_CONSOLE_PATH,
} from "@/services/clientEnvironment";
import {
  describeMinutesLeft,
  SCOPE_LABEL,
  type StaffGrant,
  useStaffGrant,
} from "@/hooks/useStaffGrant";
import { CURRENT_USER_QUERY_KEY } from "@/hooks/useCurrentUser";
import {
  type ClientEnvironmentGrant,
  endClientEnvironmentAccess,
  isRenewable,
  RENEW_URL,
  useClientEnvironment,
  useOpenStaffGrant,
} from "./clientEnvironmentAccess";
import { useMinutesLeft } from "./useMinutesLeft";

const item =
  "flex min-h-[30px] w-full cursor-pointer items-center gap-2.5 rounded-[5px] px-2 py-1 text-left text-[13px] text-[var(--fp-ink)] hover:bg-[var(--fp-hover)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--fp-border-strong)] disabled:cursor-default disabled:opacity-50";

interface ClientEnvironmentMenuProps {
  /** Returns this tab home at `path`; replaced in stories. */
  onLeft?: (path: string) => void;
  /** Where "Continue" sends this tab; replaced in stories. */
  go?: (url: string) => void;
}

const ClientEnvironmentMenu: React.FC<ClientEnvironmentMenuProps> = ({
  onLeft = goHome,
  go,
}) => {
  const environment = useClientEnvironment();
  const openGrant = useOpenStaffGrant();
  if (environment !== null) {
    return <InsideClient environment={environment} onLeft={onLeft} />;
  }
  if (openGrant !== null) {
    return <ContinueInClient grant={openGrant} go={go} />;
  }
  return null;
};

const Problem: React.FC<{ text: string | null }> = ({ text }) =>
  text === null ? null : (
    <p role="alert" className="px-2 pb-1 text-[12px] text-[var(--fp-danger)]">
      {text}
    </p>
  );

const InsideClient: React.FC<{
  environment: ClientEnvironmentGrant;
  onLeft: (path: string) => void;
}> = ({ environment, onLeft }) => {
  const queryClient = useQueryClient();
  const minutes = useMinutesLeft(environment.expiresAt);
  const [busy, setBusy] = useState<boolean>(false);
  const [problem, setProblem] = useState<string | null>(null);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });

  const act = async (
    request: () => Promise<Response | undefined>,
    failure: string
  ): Promise<boolean> => {
    setBusy(true);
    setProblem(null);
    const response = await request();
    setBusy(false);
    if (!response?.ok) {
      setProblem(failure);
      return false;
    }
    await refresh();
    return true;
  };

  const renew = (): Promise<boolean> =>
    act(() => fpFetch(RENEW_URL, { method: "POST" }), "Could not renew");

  const leave = async (): Promise<void> => {
    setBusy(true);
    setProblem(null);
    // Back to the staff console, where clients are opened (Hunter,
    // 2026-10-02).
    if (await endClientEnvironmentAccess()) {
      onLeft(STAFF_CONSOLE_PATH);
      return;
    }
    setBusy(false);
    setProblem("Could not end access");
  };

  // Admin access is taken with the staff member's own signed-in session, on
  // the open page, never with this tab's token (review 1b M2).
  const here = `${window.location.pathname}${window.location.search}`;
  const adminUrl = clientEnvironmentOpenUrl(environment.organizationId, here, {
    elevate: true,
  });

  return (
    <section
      aria-label="Client environment"
      className="border-t border-[var(--fp-border)] p-1"
    >
      <div className="px-2 pb-1 pt-1.5 text-[12px] text-[var(--fp-ink-2)]">
        <span data-scope={environment.scope}>
          {SCOPE_LABEL[environment.scope]}
        </span>
        {" · "}
        <span>{describeMinutesLeft(minutes)}</span>
      </div>
      {isRenewable(environment) && (
        <button
          type="button"
          onClick={() => void renew()}
          disabled={busy}
          className={item}
        >
          <ArrowsClockwiseIcon className="h-4 w-4" aria-hidden="true" />
          Renew
        </button>
      )}
      {environment.scope === "member" && (
        <a href={adminUrl} className={item}>
          <ShieldStarIcon className="h-4 w-4" aria-hidden="true" />
          Admin access
        </a>
      )}
      <button
        type="button"
        onClick={() => void leave()}
        disabled={busy}
        className={item}
      >
        <HouseIcon className="h-4 w-4" aria-hidden="true" />
        Exit client environment
      </button>
      <Problem text={problem} />
    </section>
  );
};

const ContinueInClient: React.FC<{
  grant: StaffGrant;
  go?: (url: string) => void;
}> = ({ grant, go }) => {
  const { continueInOpenClient } = useStaffGrant(go);
  const [busy, setBusy] = useState<boolean>(false);
  const [problem, setProblem] = useState<string | null>(null);

  const continueInClient = async (): Promise<void> => {
    setBusy(true);
    setProblem(null);
    const outcome = await continueInOpenClient();
    if (!outcome.ok) {
      setBusy(false);
      setProblem(outcome.error);
    }
  };

  return (
    <section
      aria-label="Client environment"
      className="border-t border-[var(--fp-border)] p-1"
    >
      <button
        type="button"
        onClick={() => void continueInClient()}
        disabled={busy}
        className={item}
      >
        <ArrowSquareInIcon className="h-4 w-4" aria-hidden="true" />
        <span className="min-w-0 break-words">
          Continue in {grant.organizationName}
        </span>
      </button>
      <Problem text={problem} />
    </section>
  );
};

export default ClientEnvironmentMenu;
